import { useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import {
  Video, ExternalLink, ImagePlus, X, Download,
  Copy, Check, Info, Star,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

// Best-effort image-to-video deep links. Only Kling ships a dedicated public
// URL; the other two land on the main app where the user picks the i2v tab.
const VIDEO_PLATFORMS = {
  pika: {
    name: 'Pika',
    url: 'https://pika.art/',
    supportsI2VDeepLink: false,
    bestFit: false,
  },
  kling: {
    name: 'Kling',
    url: 'https://klingai.com/global/image-to-video/new',
    supportsI2VDeepLink: true,
    bestFit: true,
  },
  runway: {
    name: 'Runway',
    url: 'https://app.runwayml.com/video-tools/teams/personal/ai-tools/generative-video',
    supportsI2VDeepLink: false,
    bestFit: false,
  },
};

const STRINGS = {
  en: {
    generateVideo: 'Generate video from this scene',
    uploadImage: 'Upload reference image',
    replaceImage: 'Replace image',
    removeImage: 'Remove',
    imageHelper: 'Add a reference image so the tool can use image-to-video mode.',
    downloadCard: 'Download scene card',
    downloadImage: 'Download reference image',
    copyPrompt: 'Copy prompt',
    copied: 'Copied!',
    sendToTool: (t) => `Open ${t}`,
    i2vTooltipDirect: (t) => `${t} opens in image-to-video mode.`,
    i2vTooltipManual: (t) => `Upload the image above into ${t}'s "Image to Video" tab for best results.`,
    bestFitLabel: 'Best fit',
    bestFitTooltip: 'Direct link — fastest workflow.',
    disclaimer: 'AIgnyx does not generate video directly — choose a tool to continue there.',
    downloadedCard: 'Scene card downloaded.',
    downloadedImage: 'Reference image downloaded.',
  },
  bg: {
    generateVideo: 'Генерирай видео от тази сцена',
    uploadImage: 'Качи референтна снимка',
    replaceImage: 'Смени снимката',
    removeImage: 'Премахни',
    imageHelper: 'Добави референтна снимка, за да можеш да използваш image-to-video режима.',
    downloadCard: 'Изтегли карта на сцената',
    downloadImage: 'Изтегли референтна снимка',
    copyPrompt: 'Копирай промпта',
    copied: 'Копирано!',
    sendToTool: (t) => `Отвори ${t}`,
    i2vTooltipDirect: (t) => `${t} се отваря директно в image-to-video режим.`,
    i2vTooltipManual: (t) => `Качи снимката по-горе в "Image to Video" раздела на ${t} за най-добри резултати.`,
    bestFitLabel: 'Най-добър избор',
    bestFitTooltip: 'Пряка връзка — най-бърз workflow.',
    disclaimer: 'AIgnyx не генерира видео директно — избери платформа, за да продължиш там.',
    downloadedCard: 'Картата на сцената е изтеглена.',
    downloadedImage: 'Референтната снимка е изтеглена.',
  },
  es: {
    generateVideo: 'Generar video desde esta escena',
    uploadImage: 'Subir imagen de referencia',
    replaceImage: 'Cambiar imagen',
    removeImage: 'Quitar',
    imageHelper: 'Añade una imagen de referencia para usar el modo image-to-video.',
    downloadCard: 'Descargar tarjeta de escena',
    downloadImage: 'Descargar imagen de referencia',
    copyPrompt: 'Copiar prompt',
    copied: '¡Copiado!',
    sendToTool: (t) => `Abrir ${t}`,
    i2vTooltipDirect: (t) => `${t} se abre en modo image-to-video.`,
    i2vTooltipManual: (t) => `Sube la imagen de arriba en la pestaña "Image to Video" de ${t}.`,
    bestFitLabel: 'Mejor opción',
    bestFitTooltip: 'Enlace directo — el workflow más rápido.',
    disclaimer: 'AIgnyx no genera video directamente — elige una plataforma para continuar allí.',
    downloadedCard: 'Tarjeta de escena descargada.',
    downloadedImage: 'Imagen de referencia descargada.',
  },
  de: {
    generateVideo: 'Video aus dieser Szene erstellen',
    uploadImage: 'Referenzbild hochladen',
    replaceImage: 'Bild ersetzen',
    removeImage: 'Entfernen',
    imageHelper: 'Füge ein Referenzbild hinzu, um den Image-to-Video-Modus zu nutzen.',
    downloadCard: 'Szenenkarte herunterladen',
    downloadImage: 'Referenzbild herunterladen',
    copyPrompt: 'Prompt kopieren',
    copied: 'Kopiert!',
    sendToTool: (t) => `${t} öffnen`,
    i2vTooltipDirect: (t) => `${t} öffnet direkt den Image-to-Video-Modus.`,
    i2vTooltipManual: (t) => `Lade das Bild oben in den "Image to Video"-Tab von ${t} hoch.`,
    bestFitLabel: 'Beste Wahl',
    bestFitTooltip: 'Direkter Link — schnellster Workflow.',
    disclaimer: 'AIgnyx erstellt keine Videos direkt — wähle eine Plattform, um dort fortzufahren.',
    downloadedCard: 'Szenenkarte heruntergeladen.',
    downloadedImage: 'Referenzbild heruntergeladen.',
  },
};

export default function SceneCard({ scene, index }) {
  const { language } = useLanguage();
  const t = STRINGS[language] || STRINGS.en;

  const fileInputRef = useRef(null);
  const captureRef = useRef(null);
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [imageName, setImageName] = useState(null);
  const [copied, setCopied] = useState(false);

  const fullSceneDescription =
    `Scene ${scene.scene_number} - ${scene.title}: ${scene.description}. ` +
    `Camera angle: ${scene.camera_angle}. Duration: ${scene.duration}. ` +
    `Visual notes: ${scene.visual_notes}`;

  const handleFilePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file (PNG, JPG, WebP).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large (max 10 MB).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageDataUrl(reader.result);
      setImageName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageDataUrl(null);
    setImageName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadDataUrl = (dataUrl, filename) => {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDownloadImage = () => {
    if (!imageDataUrl) return;
    downloadDataUrl(imageDataUrl, imageName || `scene-${scene.scene_number}-reference.png`);
    toast.success(t.downloadedImage);
  };

  const handleDownloadCard = useCallback(async () => {
    if (!captureRef.current) return;
    try {
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const dataUrl = canvas.toDataURL('image/png');
      downloadDataUrl(dataUrl, `aignyx-scene-${scene.scene_number}-card.png`);
      toast.success(t.downloadedCard);
    } catch (err) {
      console.error('Scene card render failed', err);
      toast.error('Could not render the scene card.');
    }
  }, [scene.scene_number, t.downloadedCard]);

  const handleCopyPrompt = async () => {
    await navigator.clipboard.writeText(fullSceneDescription);
    setCopied(true);
    toast.success(t.copied);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendToTool = async (platformKey) => {
    const info = VIDEO_PLATFORMS[platformKey];
    try {
      await navigator.clipboard.writeText(fullSceneDescription);
    } catch (_) {
      // Non-fatal — user may deny clipboard permission.
    }
    let tip;
    if (info.bestFit) {
      tip = `${t.bestFitTooltip} ${t.i2vTooltipDirect(info.name)}`;
    } else if (info.supportsI2VDeepLink) {
      tip = t.i2vTooltipDirect(info.name);
    } else {
      tip = t.i2vTooltipManual(info.name);
    }
    toast.success(tip);
    window.open(info.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      className="storyboard-scene"
      data-testid={`scene-${scene.scene_number}`}
    >
      <div className="dashboard-card" ref={captureRef}>
        {/* Scene info */}
        <div className="flex items-center gap-3 mb-3">
          <Badge variant="outline">Scene {scene.scene_number}</Badge>
          <span className="font-semibold">{scene.title}</span>
          <Badge className="ml-auto">{scene.duration}</Badge>
        </div>
        <p className="mb-3">{scene.description}</p>
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <span><strong>Camera:</strong> {scene.camera_angle}</span>
          <span><strong>Notes:</strong> {scene.visual_notes}</span>
        </div>

        {/* Reference image slot */}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <ImagePlus className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">{t.uploadImage}</span>
          </div>

          {imageDataUrl ? (
            <div className="relative rounded-xl overflow-hidden border border-border bg-muted/40">
              <img
                src={imageDataUrl}
                alt={`Reference for scene ${scene.scene_number}`}
                className="w-full max-h-64 object-contain"
                data-testid={`scene-${scene.scene_number}-image-preview`}
              />
              <div className="absolute top-2 right-2 flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  className="h-8 gap-1.5"
                  data-testid={`scene-${scene.scene_number}-replace-image`}
                >
                  <ImagePlus className="w-3.5 h-3.5" />
                  {t.replaceImage}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={clearImage}
                  className="h-8 gap-1.5"
                  data-testid={`scene-${scene.scene_number}-remove-image`}
                >
                  <X className="w-3.5 h-3.5" />
                  {t.removeImage}
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full rounded-xl border-2 border-dashed border-border hover:border-primary/60 hover:bg-primary/5 transition-colors py-6 flex flex-col items-center gap-2 text-sm text-muted-foreground"
              data-testid={`scene-${scene.scene_number}-upload-btn`}
            >
              <ImagePlus className="w-6 h-6 text-primary" />
              <span className="font-medium text-foreground">{t.uploadImage}</span>
              <span className="text-xs">{t.imageHelper}</span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFilePick}
            data-testid={`scene-${scene.scene_number}-file-input`}
          />
        </div>

        {/* Send-to-Video actions */}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <Video className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">{t.generateVideo}</span>
          </div>

          <div className="flex flex-wrap gap-2">
            {Object.entries(VIDEO_PLATFORMS).map(([key, info]) => {
              const tooltip = info.bestFit
                ? `${t.bestFitTooltip} ${t.i2vTooltipDirect(info.name)}`
                : t.i2vTooltipManual(info.name);
              return (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => handleSendToTool(key)}
                  className={`gap-1.5 ${info.bestFit ? 'border-primary/50 ring-1 ring-primary/20' : ''}`}
                  title={tooltip}
                  data-testid={`send-to-${key}-${scene.scene_number}`}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  {info.name}
                  {info.bestFit && (
                    <span
                      className="ml-1 inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary text-[10px] font-semibold px-1.5 py-0.5"
                      data-testid={`best-fit-badge-${key}-${scene.scene_number}`}
                    >
                      <Star className="w-2.5 h-2.5 fill-primary" />
                      {t.bestFitLabel}
                    </span>
                  )}
                </Button>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground mt-3 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{t.disclaimer}</span>
          </p>
        </div>

        {/* Download + Copy actions */}
        <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadCard}
            className="gap-1.5"
            data-testid={`scene-${scene.scene_number}-download-card`}
          >
            <Download className="w-3.5 h-3.5" />
            {t.downloadCard}
          </Button>
          {imageDataUrl && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadImage}
              className="gap-1.5"
              data-testid={`scene-${scene.scene_number}-download-image`}
            >
              <Download className="w-3.5 h-3.5" />
              {t.downloadImage}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopyPrompt}
            className="gap-1.5 ml-auto"
            data-testid={`scene-${scene.scene_number}-copy-prompt`}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? t.copied : t.copyPrompt}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
